import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance from '../../../../Utils/axiosInstance';
import { v4 as uuidv4 } from 'uuid';

import GooglePayComponent from './TrustGooglePay';
import ApplePayComponent from './TrustApplePAy';
import { buildPaymentRequest } from '../../../../Utils/paymentUtils';
import { getJWTToken, setIsApplePayAvailable } from '../../../../slices/GooglePaySlice';
import { useTranslation } from 'react-i18next';
import { getRetryLimitKey, showRetryLimitReached } from '../../../../slices/PaymentInitiationSlice';
import { paymentInitiatedEvent } from '../../../../Utils/uiAnalytics';
import { selectSelectedIntrument } from '../../../../slices/SavedInstrumentSlice';
import { selectActivePaymentMethod, selectPaymentMethodInfo } from '../../../../slices/PaymentButtonSlice';
import { getCallerUrl, getCookieValue, getIsWalletItemAvailable, getStandaloneAffirmItem, getWalletItemByInstrumentType, updateStorage } from '../../../../Utils/utils';
import { useScript } from '../../../CustomHooks/useScript';
import './walletpay.css';
import { Skeleton } from 'antd';
import RevolutPayButton from './Revolutpaybutton';
import StandaloneGooglePayButton from '../StandaloneGooglePay/StandaloneGooglePayButton';
import { GOOGLEPAY_INSTRUMENT_TYPE, isDirectGooglePayGateway } from '../StandaloneGooglePay/googlePayConfig';
import AffirmWalletButton from './AffirmWalletButton';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const TP_SCRIPT_URL = process.env.REACT_APP_TRUST_CDN;
const IS_PRODUCTION = process.env.REACT_APP_ENVIRONMENT === 'PRODUCTION';

function WalletPay() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [stInstance, setStInstance] = useState(null);
    const [stInstanceApplePay, setStInstanceApplePay] = useState(null);
    const [applePayJWT, setApplePayJWT] = useState(null);
    const formRef = useRef(null);

    // ── Selectors ────────────────────────────────────────────────────────────
    const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
    const updatedShopperDetails = useSelector((state) => state.paymentSessionDetails.updatedShopperDetails);
    const appliedOfferDetail = useSelector((state) => state.offers.appliedOfferDetail);
    const totalPaymentSummaryValue = useSelector((state) => state.additionalComponents.totalPaymentSummaryValue);
    const showTotalPaymentSummaryField = useSelector((state) => state.additionalComponents.showTotalPaymentSummaryField);
    const paymentMethodDictionaryAll = useSelector((state) => state.paymentSessionDetails.paymentMethodDictionaryAll);
    const surchargeDetail = useSelector(state => state.surcharges.surchargeDetail);

    const selectedInstrument = useSelector((state) => selectSelectedIntrument(state));
    const paymentMethodInfo = useSelector((state) => selectPaymentMethodInfo(state));
    const googlePayJWT = useSelector((state) => state.gpayDetails?.JWT);
    const isGpayDetailsLoading = useSelector((state) => state.gpayDetails?.isGpayDetailsLoading);
    const activePaymentMethod = useSelector((state) => selectActivePaymentMethod(state));

    const isLoaded = useScript(TP_SCRIPT_URL);
    const [isFormSubmittingApplePay, setIsFormSubmittingApplePay] = useState(false);
    const [isFormSubmittingGooglePay, setIsFormSubmittingGooglePay] = useState(false);
    const [showErrorModalApplePay, setShowErrorModalApplePay] = useState(false);
    const [showErrorModalGooglePay, setShowErrorModalGooglePay] = useState(false);

    const notificationObserverRef = useRef(null); // 👈 New ref for notification frame observer
    const googlePayEntry = getWalletItemByInstrumentType(paymentMethodDictionaryAll, GOOGLEPAY_INSTRUMENT_TYPE);
    const isGooglePayDirectAvailable = isDirectGooglePayGateway(googlePayEntry?.additionalData);
    const isGooglePayAvailable = (googlePayEntry && !isGooglePayDirectAvailable) ? googlePayEntry.additionalData : false
    const ApplePayAvailable = getIsWalletItemAvailable(paymentMethodDictionaryAll, "ApplePay")
    const isApplePayAvailable = ApplePayAvailable?.additionalData || false
    const isRevolutPayAvailable = getIsWalletItemAvailable(paymentMethodDictionaryAll, "RevolutPay")
    // Express Affirm button — driven by whether the session actually advertises
    // Affirm now, same as the wallet brands above.
    const SHOW_AFFIRM_EXPRESS = !!getStandaloneAffirmItem(paymentMethodDictionaryAll)

    const getSurchargesFor = useCallback((networkKey, applicableOn = 'Wallet') => {
        return surchargeDetail?.appliedSurcharges?.filter((surcharge) => {
            const details = surcharge?.surchargeDetails;
            if (details?.applicableOn && details.applicableOn !== applicableOn) return false;
            const network = details?.network?.toLowerCase();
            return !network || network === networkKey;
        }) ?? [];
    }, [surchargeDetail]);

    const googlePaySurcharges = useMemo(() => getSurchargesFor('googlepay'), [getSurchargesFor]);
    const applePaySurcharges = useMemo(() => getSurchargesFor('applepay'), [getSurchargesFor]);
    const revolutPaySurcharges = useMemo(() => getSurchargesFor('revolutpay'), [getSurchargesFor]);
    const affirmSurcharges = useMemo(() => getSurchargesFor('affirm', 'BuyNowPayLater'), [getSurchargesFor]);

    const walletSurchargeIds = useMemo(
        () => googlePaySurcharges.map((s) => s.surchargeDetails?.surchargeCode),
        [googlePaySurcharges]
    );

    const affirmSurchargeAmount = useMemo(
        () => affirmSurcharges.reduce((sum, s) => sum + (Number(s?.calculatedSurchargeFee) || 0), 0),
        [affirmSurcharges]
    );

    const surchargesByType = {
        'wallet/googlepay': googlePaySurcharges,
        'wallet/applepay': applePaySurcharges,
        'wallet/revolutpay': revolutPaySurcharges,
        'bnpl/affirm': affirmSurcharges,
    };

    // Set while the retry-limit modal owns the error UI. Rejecting the Google/Apple Pay
    // sheet makes Trust print "cancelled" in the notification frame, which would
    // otherwise stack the generic error modal on top of (or after) the retry-limit one.
    // Reset on the next wallet button click (see clearFormAction).
    const retryLimitReachedRef = useRef(false);

    // Retry limit (BE_1815 / BE_1816) → open the global retry-limit modal in BoxPayCheckout.
    const openRetryLimitModal = (reasonCode, apiMessage) => {
        if (!getRetryLimitKey(reasonCode, apiMessage)) return false;
        retryLimitReachedRef.current = true;
        dispatch(showRetryLimitReached({ reasonCode, message: apiMessage }));
        return true;
    };

    const handleInitiatePayment = async (type, walletToken = '', errorMsg = '') => {
        const typeSurchargeIds = (surchargesByType[type] ?? []).map((s) => s.surchargeDetails?.surchargeCode);
        const paymentRequest = buildPaymentRequest(
            type,
            updatedShopperDetails,
            sessionDetails,
            '',
            appliedOfferDetail?.codes,
            typeSurchargeIds,
        );

        if (totalPaymentSummaryValue) {
            paymentRequest.payload.money = {
                amount: totalPaymentSummaryValue,
                currencyCode: sessionDetails?.paymentDetails?.money?.currencyCode,
            };
        }
        paymentRequest.payload.instrumentDetails.wallet = { token: walletToken };
        if (errorMsg) {
            paymentRequest.payload.instrumentDetails.wallet.errorMsg = errorMsg;
        }
        const { token, payload } = paymentRequest;

        if (!token) {
            console.warn("Wallet payment initiation blocked: missing checkout session token");
            return { success: false };
        }

        try {
            const response = await axiosInstance.post(
                `${BASE_URL}/checkout/sessions/${token}`,
                payload,
                {
                    headers: {
                        'X-Request-Id': uuidv4(),
                        'X-Caller-Url': getCallerUrl(),
                        Authorization: `Session ${getCookieValue('shopper_token') || localStorage.getItem('sessionToken')}`
                    }
                }
            );
            const rejectedReasonCode = response?.data?.status?.reasonCode;
            const rejectedReason = response?.data?.status?.reason;
            if (openRetryLimitModal(rejectedReasonCode, rejectedReason)) {
                return { success: false, retryLimitReached: true, message: t(getRetryLimitKey(rejectedReasonCode, rejectedReason)) };
            }
            updateStorage("session", "update", "t_id", response?.data.transactionId);
            const formActionUrl = response?.data?.actions?.[0]?.url;
            if (formActionUrl && formRef.current) {
                formRef.current.action = formActionUrl;
            }


            return { success: !!formActionUrl, formActionUrl, data: response?.data, message: response?.data?.status?.reason };
        } catch (err) {
            console.log("Payment initiation failed:", err);
            const errReasonCode = err?.response?.data?.reasonCode;
            const errApiMessage = err?.response?.data?.message;
            if (openRetryLimitModal(errReasonCode, errApiMessage)) {
                return { success: false, retryLimitReached: true, message: t(getRetryLimitKey(errReasonCode, errApiMessage)) };
            }
            return { success: false, message: err?.response?.data?.message };
        }
    }

    const handleInitiatePaymentAnalytics = async (instrumentTypeValue, errorInfo) => {
        paymentInitiatedEvent(
            'walletData',
            instrumentTypeValue || "WalletPay",
            errorInfo
        );
    }

    // ── SDK Initialization (The Fix) ─────────────────────────────────────────
    useEffect(() => {
        if (typeof window.ApplePaySession === 'undefined' || !window.ApplePaySession.canMakePayments()) {
            dispatch(setIsApplePayAvailable(false))
            console.warn('[ApplePay] 🚫 Not supported on this browser/device');
        }
    }, [dispatch]);

    useEffect(() => {
        if (isLoaded && googlePayJWT && window.SecureTrading && !stInstance && isGooglePayAvailable) {

            const st = window.SecureTrading({
                jwt: googlePayJWT,
                livestatus: IS_PRODUCTION ? 1 : 0,
                translations: {
                    'Payment has been cancelled': 'Cancelled',
                },
            });

            st.on('paymentCompleted', (data) => {
                console.log('[WalletPay] ✅ Global Success:', data);
            });

            st.on('paymentFailed', (data) => {
                console.error('[WalletPay] ❌ Global Failure:', data);
            });

            setStInstance(st);
        }
    }, [isLoaded, googlePayJWT, stInstance, isGooglePayAvailable]);


    useEffect(() => {
        if (sessionDetails?.token && (isGooglePayDirectAvailable || isGooglePayAvailable)) {
            setStInstance(null);
            dispatch(getJWTToken({
                token: sessionDetails.token,
                surcharge: { surchargeIds: walletSurchargeIds },
            }));
        }
    }, [sessionDetails?.token, JSON.stringify(walletSurchargeIds), dispatch, isGooglePayAvailable]);

    const applePaySurchargeIds = useMemo(
        () => applePaySurcharges.map((s) => s.surchargeDetails?.surchargeCode),
        [applePaySurcharges]
    );

    useEffect(() => {
        if (!sessionDetails?.token || !ApplePayAvailable) return;
        setStInstanceApplePay(null);
        axiosInstance.post(
            `${BASE_URL}/checkout/sessions/${sessionDetails.token}/applepay/details`,
            { surchargeIds: applePaySurchargeIds },
        ).then((response) => {
            setApplePayJWT(response?.data?.token);
        }).catch((err) => {
            console.log('Apple Pay JWT fetch failed:', err);
        });
    }, [sessionDetails?.token, JSON.stringify(applePaySurchargeIds), ApplePayAvailable]);

    useEffect(() => {
        if (isLoaded && applePayJWT && window.SecureTrading && !stInstanceApplePay && isApplePayAvailable) {
            const stApplePay = window.SecureTrading({
                jwt: applePayJWT,
                livestatus: IS_PRODUCTION ? 1 : 0,
                translations: {
                    'Payment has been cancelled': 'Cancelled',
                },
            });

            stApplePay.on('paymentCompleted', (data) => {
                console.log('[WalletPay] ✅ Apple Pay Success:', data);
            });

            stApplePay.on('paymentFailed', (data) => {
                console.error('[WalletPay] ❌ Apple Pay Failure:', data);
            });

            setStInstanceApplePay(stApplePay);
        }
    }, [isLoaded, applePayJWT, stInstanceApplePay, isApplePayAvailable]);

    // 👇 Observe st-notification-frame for "cancelled" text
    const handleCancelledNotification = (text) => {
        if (retryLimitReachedRef.current) return;
        if (isFormSubmittingApplePay) {
            handleInitiatePaymentAnalytics("ApplePay/paymentCanceled", `Cancellation detected via notification frame: "${text}"`);
            setShowErrorModalApplePay(true);
            setIsFormSubmittingApplePay(false);
        } else {
            handleInitiatePaymentAnalytics("googlePay/paymentCanceled", `Cancellation detected via notification frame: "${text}"`);
            setIsFormSubmittingGooglePay(false);
            setShowErrorModalGooglePay(true);
        }
    };

    const clearFormAction = useCallback(() => {
        // Called on every Google/Apple Pay button click → new attempt, re-enable generic error handling.
        retryLimitReachedRef.current = false;
        if (formRef.current) {
            // 1. Remove the action URL
            formRef.current.removeAttribute('action');
            // 2. Cleanup: Remove the hidden JWT input if it was previously appended
            const existingJwt = formRef.current.querySelector('input[name="jwt"]');
            if (existingJwt) {
                formRef.current.removeChild(existingJwt);
            }
        }
    }, [formRef]);

    useEffect(() => {
        const notificationFrame = document.getElementById('st-notification-frame');
        if (!notificationFrame) return;

        notificationObserverRef.current = new MutationObserver(() => {
            const text = notificationFrame.textContent?.toLowerCase() ?? '';
            const hasChildWithCancelled = Array.from(notificationFrame.querySelectorAll('*')).some(
                (el) => el.textContent?.toLowerCase().includes('cancelled')
            );

            if (text.includes('cancelled') || hasChildWithCancelled) {
                handleCancelledNotification(text);
            }
        });

        notificationObserverRef.current.observe(notificationFrame, {
            childList: true,   // Watch for added/removed child nodes
            subtree: true,     // Watch all descendants
            characterData: true, // Watch for text content changes
        });

        return () => {
            notificationObserverRef.current?.disconnect();
        };
    }, []); // Runs once on mount

    // ── Render ───────────────────────────────────────────────────────────────
    const applePayShowing = !!(window?.ApplePaySession && window?.ApplePaySession?.canMakePayments() && isApplePayAvailable);
    const buttonCount = [isGooglePayAvailable, isGooglePayDirectAvailable, !!isRevolutPayAvailable, applePayShowing, SHOW_AFFIRM_EXPRESS].filter(Boolean).length;
    const walletCols = buttonCount >= 4 ? 2 : Math.max(buttonCount, 1);
    const isOdd = buttonCount % 2 !== 0;
    const isGooglePayLoading = isGooglePayAvailable && !stInstance;
    const isApplePayLoading = applePayShowing && !stInstanceApplePay;
    const isAnyWalletLoading = isGooglePayLoading || isApplePayLoading;

    return (
        <div className="wallet-pay-container " style={{ width: "100%", position: "relative" }}>
            <form ref={formRef} id="st-form" method="POST" action="" style={{ width: "100%" }}>
                <div id="st-notification-frame" style={{ display: "none" }}></div>

                <div className={`wallet-container${isOdd ? ' wallet-odd' : ''}`} style={{
                    gridTemplateColumns: `repeat(${walletCols}, minmax(0, 1fr))`,
                    '--wallet-cols': walletCols,
                }}>
                    {/* 1. Google Pay (Trust) — only when additionalData.gateway isn't "boxpay" */}
                    {isGooglePayAvailable && stInstance && (
                        <GooglePayComponent
                            jwt={googlePayJWT}
                            formRef={formRef}
                            st={stInstance}
                            handleInitiatePayment={handleInitiatePayment}
                            showErrorModal={showErrorModalGooglePay}
                            setShowErrorModal={setShowErrorModalGooglePay}
                            isFormSubmitting={isFormSubmittingGooglePay}
                            setIsFormSubmitting={setIsFormSubmittingGooglePay}
                            clearFormAction={clearFormAction}
                            handleInitiatePaymentAnalytics={handleInitiatePaymentAnalytics}
                            walletSurcharges={googlePaySurcharges}
                            totalPaymentSummaryValue={totalPaymentSummaryValue}
                        />
                    )}

                    {/* Standalone Google Pay — only when additionalData.gateway === "boxpay" */}
                    {isGooglePayDirectAvailable && (
                        <StandaloneGooglePayButton />
                    )}

                    {/* 2. Revolut Pay */}
                    {isRevolutPayAvailable && (
                        <div style={{ display: isAnyWalletLoading ? "none" : "block", overflow: "hidden", minWidth: 0 }}>
                            <RevolutPayButton handleInitiatePayment={handleInitiatePayment} handleInitiatePaymentAnalytics={handleInitiatePaymentAnalytics} />
                        </div>
                    )}

                    {/* 3. Apple Pay */}
                    {applePayShowing && stInstanceApplePay && (
                        <ApplePayComponent
                            clearFormAction={clearFormAction}
                            st={stInstanceApplePay}
                            formRef={formRef}
                            jwt={applePayJWT}
                            handleInitiatePayment={handleInitiatePayment}
                            showErrorModal={showErrorModalApplePay}
                            setShowErrorModal={setShowErrorModalApplePay}
                            isFormSubmitting={isFormSubmittingApplePay}
                            setIsFormSubmitting={setIsFormSubmittingApplePay}
                            handleInitiatePaymentAnalytics={handleInitiatePaymentAnalytics}
                            walletSurcharges={applePaySurcharges}
                            totalPaymentSummaryValue={totalPaymentSummaryValue}
                        />
                    )}

                    {/* 4. Affirm (VCN) — hardcoded for now */}
                    {SHOW_AFFIRM_EXPRESS && (
                        <div style={{ display: isAnyWalletLoading ? "none" : "block", overflow: "hidden", minWidth: 0 }}>
                            <AffirmWalletButton
                                handleInitiatePayment={handleInitiatePayment}
                                handleInitiatePaymentAnalytics={handleInitiatePaymentAnalytics}
                                surchargeAmount={affirmSurchargeAmount}
                            />
                        </div>
                    )}

                </div>
                {isAnyWalletLoading && (
                    <div className='skeletonMain'>
                        <div className='skeletonContainer'>
                            <Skeleton.Button style={{ width: "100%", height: "100%", display: "block" }} active block />
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
}

export default WalletPay;
